import { useEffect, useState, useCallback } from "react";
import {
  msEmailStatus, msEmailLoginUrl, msEmailDisconnect, getSmtp, saveSmtp, sendTestEmail,
  getEmailPref, setEmailPref, listMsSenders, renameMsSender, deleteMsSender, isAdminUser,
  aiGetOpenAiKey, aiSetOpenAiKey, aiDeleteOpenAiKey, aiSetDefaultProvider,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import OrderEmailAutomation from "@/components/OrderEmailAutomation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Loader2, CheckCircle2, AlertCircle, Mail, Send, X, Plus, Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";

const MsLogo = () => (
  <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
    <rect x="1" y="1" width="10" height="10" fill="#F25022" />
    <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
    <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
    <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
  </svg>
);
const GmailLogo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M2 6.5A1.5 1.5 0 0 1 3.5 5h.7L12 11l7.8-6h.7A1.5 1.5 0 0 1 22 6.5V18a1.5 1.5 0 0 1-1.5 1.5H19V9.3l-7 5.4-7-5.4V19.5H3.5A1.5 1.5 0 0 1 2 18V6.5Z" />
    <path fill="#EA4335" d="M2 6.5 12 14l10-7.5V6.5A1.5 1.5 0 0 0 20.5 5h-.7L12 11 4.2 5h-.7A1.5 1.5 0 0 0 2 6.5Z" />
  </svg>
);

const ConnBadge = ({ on }) =>
  on ? (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">
      <CheckCircle2 size={10} /> Connected
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">
      <AlertCircle size={10} /> Not set
    </span>
  );

export default function EmailSending({ userRecordId, defaultEmail }) {
  const [loading, setLoading] = useState(true);
  // Outlook / Microsoft
  const [ms, setMs] = useState({ connected: false, email: "" });
  const [connecting, setConnecting] = useState(false);
  // Gmail (SMTP)
  const [gmailSet, setGmailSet] = useState(false);
  const [gmailUser, setGmailUser] = useState("");
  const [editingGmail, setEditingGmail] = useState(false);
  const [gmailPwd, setGmailPwd] = useState("");
  const [gmailEmail, setGmailEmail] = useState(defaultEmail || "");
  const [savingGmail, setSavingGmail] = useState(false);
  // Test send
  const [testTo, setTestTo] = useState(defaultEmail || "");
  const [testing, setTesting] = useState(false);
  // Default sender preference ('outlook' | 'gmail' | '')
  const [pref, setPref] = useState("");
  const [savingPref, setSavingPref] = useState(false);

  const anyConnected = ms.connected || gmailSet;
  const effectivePref = pref || (ms.connected ? "outlook" : gmailSet ? "gmail" : "");

  const load = useCallback(() => {
    if (!userRecordId) return;
    setLoading(true);
    Promise.all([
      msEmailStatus(userRecordId).catch(() => ({})),
      getSmtp(userRecordId).catch(() => ({})),
      getEmailPref(userRecordId).catch(() => ({})),
    ])
      .then(([m, s, p]) => {
        setMs({ connected: !!m?.connected, email: m?.email || "" });
        const sm = s?.smtp || {};
        const isGmail = (sm.smtp_host || "").includes("gmail");
        setGmailSet(!!sm.smtp_password_set && isGmail);
        setGmailUser(isGmail ? sm.smtp_username || "" : "");
        if (isGmail && sm.smtp_username) setGmailEmail(sm.smtp_username);
        setPref(p?.method || "");
      })
      .finally(() => setLoading(false));
  }, [userRecordId]);

  async function savePref(method) {
    if (savingPref) return;
    setSavingPref(true);
    const prev = pref;
    setPref(method);
    try {
      await setEmailPref(userRecordId, method);
      toast.success(`Campaigns will send from ${method === "outlook" ? "Outlook" : "Gmail"}`);
    } catch (e) {
      setPref(prev);
      toast.error(e?.response?.data?.detail || "Could not save preference");
    } finally {
      setSavingPref(false);
    }
  }

  useEffect(() => { load(); }, [load]);

  // Toast on return from Microsoft OAuth callback.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("microsoft");
    if (flag === "connected") { toast.success("Outlook connected"); load(); }
    else if (flag === "error") toast.error("Could not connect Outlook. Try again.");
    if (flag) {
      params.delete("microsoft");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, [load]);

  async function connectMs() {
    setConnecting(true);
    try {
      window.location.href = await msEmailLoginUrl(userRecordId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Microsoft email isn't configured");
      setConnecting(false);
    }
  }
  async function disconnectMs() {
    try { await msEmailDisconnect(userRecordId); setMs({ connected: false, email: "" }); toast.success("Outlook disconnected"); }
    catch { toast.error("Could not disconnect"); }
  }

  async function saveGmail() {
    if (!gmailEmail) return toast.error("Enter your Gmail address");
    if (!gmailPwd && !gmailSet) return toast.error("Enter your Gmail app password");
    setSavingGmail(true);
    try {
      const payload = {
        user_record_id: userRecordId,
        smtp_host: "smtp.gmail.com",
        smtp_port: 587,
        smtp_username: gmailEmail,
      };
      if (gmailPwd) payload.smtp_password = gmailPwd;
      const res = await saveSmtp(payload);
      setGmailSet(!!res.smtp?.smtp_password_set);
      setGmailUser(gmailEmail);
      setGmailPwd("");
      setEditingGmail(false);
      toast.success("Gmail saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSavingGmail(false); }
  }

  async function runTest() {
    if (!testTo || !testTo.includes("@")) return toast.error("Enter a recipient email");
    setTesting(true);
    try {
      const res = await sendTestEmail(userRecordId, testTo);
      toast.success(`Test email sent via ${res.method === "outlook" ? "Outlook" : "Gmail/SMTP"} ✓`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test email failed");
    } finally { setTesting(false); }
  }

  return (
    <Card className="rounded-2xl border-zinc-200 p-4" data-testid="email-sending-card">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
          <Mail className="text-violet-700" size={18} />
        </div>
        <div>
          <div className="font-semibold text-zinc-900">Email sending</div>
          <p className="text-xs text-zinc-500">Connect a mailbox to send campaigns from your own address.</p>
        </div>
      </div>

      {loading ? (
        <div className="py-5 flex items-center justify-center">
          <Loader2 className="animate-spin text-zinc-400" size={18} />
        </div>
      ) : (
        <div className="mt-3 divide-y divide-zinc-100">
          {/* Outlook / Microsoft */}
          <div className="flex items-center justify-between gap-3 py-3" data-testid="ms-email-row">
            <div className="flex items-center gap-2 min-w-0">
              <MsLogo />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-800 flex items-center gap-2">
                  Outlook <ConnBadge on={ms.connected} />
                </div>
                {ms.connected && ms.email && (
                  <div className="text-xs text-zinc-500 truncate" data-testid="ms-connected-email">{ms.email}</div>
                )}
              </div>
            </div>
            {ms.connected ? (
              <Button variant="outline" onClick={disconnectMs} className="rounded-lg h-9 shrink-0" data-testid="ms-disconnect-btn">
                Disconnect
              </Button>
            ) : (
              <Button onClick={connectMs} disabled={connecting} className="rounded-lg h-9 shrink-0 bg-white border border-zinc-300 text-zinc-800 hover:bg-zinc-50 gap-1.5" data-testid="ms-connect-btn">
                {connecting ? <Loader2 className="animate-spin" size={14} /> : <MsLogo />} Connect
              </Button>
            )}
          </div>

          {/* Gmail */}
          <div className="py-3" data-testid="gmail-row">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <GmailLogo />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-800 flex items-center gap-2">
                    Gmail <ConnBadge on={gmailSet} />
                  </div>
                  {gmailSet && gmailUser && (
                    <div className="text-xs text-zinc-500 truncate" data-testid="gmail-connected-email">{gmailUser}</div>
                  )}
                </div>
              </div>
              {!editingGmail && (
                <Button variant="outline" onClick={() => setEditingGmail(true)} className="rounded-lg h-9 shrink-0" data-testid="gmail-edit-btn">
                  {gmailSet ? "Edit" : "Connect"}
                </Button>
              )}
            </div>

            {editingGmail && (
              <div className="mt-3 space-y-2" data-testid="gmail-form">
                <Input
                  value={gmailEmail}
                  onChange={(e) => setGmailEmail(e.target.value)}
                  placeholder="you@gmail.com"
                  className="h-10 rounded-lg"
                  data-testid="gmail-email-input"
                />
                <Input
                  type="password"
                  value={gmailPwd}
                  onChange={(e) => setGmailPwd(e.target.value)}
                  placeholder={gmailSet ? "App password (saved — leave blank to keep)" : "Gmail app password"}
                  className="h-10 rounded-lg"
                  data-testid="gmail-password-input"
                />
                <div className="rounded-lg border border-amber-100 bg-amber-50/70 p-3 text-[11px] text-amber-900 leading-relaxed" data-testid="gmail-app-password-help">
                  <div className="font-semibold mb-1">This is a Google App Password — not your Gmail login password.</div>
                  <ol className="list-decimal pl-4 space-y-0.5">
                    <li>Turn on 2-Step Verification for your Google account (required).</li>
                    <li>Open <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="font-semibold underline" data-testid="gmail-app-password-link">myaccount.google.com/apppasswords</a>, enter a name like “OreVital” and press Create.</li>
                    <li>Copy the 16-character password Google shows (spaces don't matter) and paste it above.</li>
                  </ol>
                  <div className="mt-1 text-amber-800/80">Host smtp.gmail.com:587 is set automatically. Google Workspace admins may need to allow App Passwords.</div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveGmail} disabled={savingGmail} className="rounded-lg h-9 bg-violet-600 hover:bg-violet-700 text-white" data-testid="gmail-save-btn">
                    {savingGmail ? <Loader2 className="animate-spin" size={14} /> : "Save"}
                  </Button>
                  <Button variant="outline" onClick={() => { setEditingGmail(false); setGmailPwd(""); }} className="rounded-lg h-9">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Default sender for CRM emails */}
          {(ms.connected || gmailSet) && (
            <div className="py-3" data-testid="email-default-sender-row">
              <div className="text-sm font-semibold text-zinc-800 mb-2">
                Default sender for CRM
              </div>
              <p className="text-[11px] text-zinc-500 mb-2">
                When you email or message contacts in your CRM, send from this mailbox.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={!ms.connected || savingPref}
                  onClick={() => savePref("outlook")}
                  className={`flex items-center justify-center gap-1.5 h-10 rounded-lg border text-sm font-medium transition-colors ${
                    effectivePref === "outlook"
                      ? "border-violet-500 bg-violet-50 text-violet-700"
                      : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                  } disabled:opacity-40 disabled:cursor-not-allowed`}
                  data-testid="email-pref-outlook"
                >
                  <MsLogo /> Outlook
                  {effectivePref === "outlook" && <CheckCircle2 size={14} className="text-violet-600" />}
                </button>
                <button
                  type="button"
                  disabled={!gmailSet || savingPref}
                  onClick={() => savePref("gmail")}
                  className={`flex items-center justify-center gap-1.5 h-10 rounded-lg border text-sm font-medium transition-colors ${
                    effectivePref === "gmail"
                      ? "border-violet-500 bg-violet-50 text-violet-700"
                      : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                  } disabled:opacity-40 disabled:cursor-not-allowed`}
                  data-testid="email-pref-gmail"
                >
                  <GmailLogo /> Gmail
                  {effectivePref === "gmail" && <CheckCircle2 size={14} className="text-violet-600" />}
                </button>
              </div>
            </div>
          )}

          {/* Send test email */}
          <div className="py-3" data-testid="email-test-row">
            <div className="text-sm font-semibold text-zinc-800 mb-2 flex items-center gap-1.5">
              <Send size={14} className="text-violet-600" /> Send a test email
            </div>
            <div className="flex gap-2">
              <Input
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="recipient@example.com"
                className="h-10 rounded-lg flex-1"
                data-testid="email-test-input"
              />
              <Button
                onClick={runTest}
                disabled={testing || !anyConnected}
                className="rounded-lg h-10 bg-zinc-900 hover:bg-zinc-800 text-white shrink-0"
                data-testid="email-test-btn"
              >
                {testing ? <Loader2 className="animate-spin" size={14} /> : "Test"}
              </Button>
            </div>
            {!anyConnected && (
              <p className="text-[11px] text-amber-600 mt-1">Connect Outlook or save Gmail above first.</p>
            )}
          </div>
        </div>
      )}

      <AdminSmtpSenders userRecordId={userRecordId} />
      <OrderEmailAutomation userRecordId={userRecordId} />
      <AdminOpenAiKey userRecordId={userRecordId} />
    </Card>
  );
}

function AdminSmtpSenders({ userRecordId }) {
  const { user } = useAuth();
  const admin = isAdminUser(user);
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [labelDraft, setLabelDraft] = useState("");

  const load = useCallback(() => {
    if (!admin || !userRecordId) { setLoading(false); return; }
    setLoading(true);
    listMsSenders(userRecordId)
      .then(setSenders)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [admin, userRecordId]);
  useEffect(() => { load(); }, [load]);

  if (!admin) return null;

  async function addMailbox() {
    setConnecting(true);
    try {
      window.location.href = await msEmailLoginUrl(userRecordId, "extra");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Microsoft email isn't configured");
      setConnecting(false);
    }
  }

  async function remove(id) {
    try {
      await deleteMsSender(userRecordId, id);
      setSenders((s) => s.filter((x) => x.id !== id));
      toast.success("Mailbox removed");
    } catch {
      toast.error("Could not remove mailbox");
    }
  }

  function startEdit(s) {
    setEditingId(s.id);
    setLabelDraft(s.label || "");
  }

  async function saveLabel(id) {
    try {
      await renameMsSender(userRecordId, id, labelDraft.trim());
      setSenders((s) => s.map((x) => (x.id === id ? { ...x, label: labelDraft.trim() } : x)));
      setEditingId(null);
      toast.success("Label saved");
    } catch {
      toast.error("Could not save label");
    }
  }

  return (
    <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="admin-smtp-senders">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-zinc-800 flex items-center gap-1.5">
            <Mail size={14} /> Extra send-from addresses
          </div>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Admin only — sign in to Outlook to add more mailboxes you can pick from when sending.
          </p>
        </div>
        <Button
          size="sm"
          onClick={addMailbox}
          disabled={connecting}
          className="rounded-lg h-9 shrink-0 bg-white border border-zinc-300 text-zinc-800 hover:bg-zinc-50 gap-1.5"
          data-testid="ms-sender-add-btn"
        >
          {connecting ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
          Add mailbox
        </Button>
      </div>

      {loading ? (
        <div className="py-4 flex justify-center"><Loader2 className="animate-spin text-zinc-400" size={16} /></div>
      ) : (
        <div className="mt-3 space-y-2">
          {senders.map((s) => (
            <div key={s.id} className="flex items-center gap-2 rounded-lg border border-zinc-200 px-3 py-2" data-testid={`ms-sender-${s.id}`}>
              <MsLogo />
              {editingId === s.id ? (
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <Input
                    autoFocus
                    value={labelDraft}
                    onChange={(e) => setLabelDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveLabel(s.id); }}
                    placeholder="Label (e.g. Sales)"
                    className="h-8 rounded-lg text-sm"
                    data-testid={`ms-sender-label-input-${s.id}`}
                  />
                  <Button size="sm" onClick={() => saveLabel(s.id)} className="rounded-lg h-8 bg-violet-600 hover:bg-violet-700 text-white" data-testid={`ms-sender-label-save-${s.id}`}>
                    Save
                  </Button>
                </div>
              ) : (
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zinc-900 truncate">
                    {s.label || s.email}
                  </div>
                  {s.label && <div className="text-[11px] text-zinc-500 truncate">{s.email}</div>}
                </div>
              )}
              {editingId !== s.id && (
                <button onClick={() => startEdit(s)} className="text-zinc-400 hover:text-zinc-700 p-1" title="Rename" data-testid={`ms-sender-edit-${s.id}`}>
                  <Pencil size={14} />
                </button>
              )}
              <button onClick={() => remove(s.id)} className="text-rose-400 hover:text-rose-600 p-1" title="Remove" data-testid={`ms-sender-del-${s.id}`}>
                <X size={15} />
              </button>
            </div>
          ))}
          {senders.length === 0 && (
            <p className="text-[11px] text-zinc-400">No extra mailboxes yet. Click "Add mailbox" to connect one.</p>
          )}
        </div>
      )}
    </div>
  );
}


function AdminOpenAiKey({ userRecordId }) {
  const { user } = useAuth();
  const admin = isAdminUser(user);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [hint, setHint] = useState("");
  const [keyDraft, setKeyDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [defaultProvider, setDefaultProvider] = useState("emergent");

  const load = useCallback(() => {
    if (!admin || !userRecordId) { setLoading(false); return; }
    aiGetOpenAiKey(userRecordId)
      .then((r) => { setConnected(!!r.connected); setHint(r.hint || ""); setDefaultProvider(r.default_provider || "emergent"); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [admin, userRecordId]);

  useEffect(() => { load(); }, [load]);

  if (!admin) return null;

  async function save() {
    if (!keyDraft.trim().startsWith("sk-")) { toast.error("Enter a valid OpenAI key (starts with sk-)"); return; }
    setSaving(true);
    try {
      const r = await aiSetOpenAiKey(userRecordId, keyDraft.trim());
      setConnected(true); setHint(r.hint || ""); setKeyDraft(""); setReplacing(false);
      toast.success("OpenAI account connected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save key");
    } finally { setSaving(false); }
  }

  async function setDefault(p) {
    try {
      const r = await aiSetDefaultProvider(userRecordId, p);
      setDefaultProvider(r.default_provider);
      toast.success(p === "openai" ? "OpenAI is now the default AI engine for everyone" : "OreVital AI is now the default engine");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update default engine");
    }
  }

  async function disconnect() {
    try {
      await aiDeleteOpenAiKey(userRecordId);
      setConnected(false); setHint(""); setDefaultProvider("emergent");
      toast.success("OpenAI account disconnected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disconnect");
    }
  }

  return (
    <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="admin-openai-key">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={15} className="text-violet-500" />
        <span className="text-sm font-semibold text-zinc-800">OpenAI account (AI email writer)</span>
        <ConnBadge on={connected} />
      </div>
      <p className="text-[11px] text-zinc-400 mb-2">
        Optional. Add your own OpenAI API key to run the AI email writer on your OpenAI billing
        (uses GPT-5.6). Leave empty to use the built-in low-cost OreVital AI. Get a key at
        platform.openai.com → API keys.
      </p>
      {loading ? (
        <Loader2 className="animate-spin text-zinc-400" size={16} />
      ) : connected && !replacing ? (
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-600 font-mono">{hint || "sk-…"}</span>
          <Button size="sm" variant="ghost" className="h-8 rounded-lg text-violet-700 hover:bg-violet-50"
            onClick={() => setReplacing(true)} data-testid="openai-key-replace">
            Replace key
          </Button>
          <Button size="sm" variant="ghost" className="h-8 rounded-lg text-rose-600 hover:bg-rose-50"
            onClick={disconnect} data-testid="openai-key-disconnect">
            <X size={13} className="mr-1" /> Disconnect
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Input type="password" value={keyDraft} onChange={(e) => setKeyDraft(e.target.value)}
            placeholder={connected ? `New key (replaces ${hint || "current"})` : "sk-…"} className="h-9 rounded-lg text-sm font-mono" data-testid="openai-key-input" />
          <Button size="sm" className="h-9 rounded-lg bg-violet-600 hover:bg-violet-700 text-white"
            disabled={saving} onClick={save} data-testid="openai-key-save">
            {saving ? <Loader2 className="animate-spin" size={14} /> : "Save"}
          </Button>
          {connected && (
            <Button size="sm" variant="ghost" className="h-9 rounded-lg" onClick={() => { setReplacing(false); setKeyDraft(""); }} data-testid="openai-key-replace-cancel">
              Cancel
            </Button>
          )}
        </div>
      )}
      {connected && !loading && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid="ai-default-engine">
          <span className="text-zinc-600 font-medium">Default AI engine for everyone:</span>
          <div className="flex rounded-lg bg-zinc-100 p-0.5">
            <button type="button" onClick={() => setDefault("emergent")}
              className={`px-2.5 h-7 rounded-md font-medium ${defaultProvider === "emergent" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`}
              data-testid="ai-default-engine-emergent">OreVital AI (low cost)</button>
            <button type="button" onClick={() => setDefault("openai")}
              className={`px-2.5 h-7 rounded-md font-medium ${defaultProvider === "openai" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`}
              data-testid="ai-default-engine-openai">OpenAI (GPT-5.6)</button>
          </div>
          <span className="text-zinc-400">Dealers can still switch per request.</span>
        </div>
      )}
    </div>
  );
}
